import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import I18n, { Dir, LANGUAGES, LanguageMeta, LocaleCode, STORAGE_KEY, detectInitialLocale, dirFor } from './config';

interface LanguageContextValue {
	locale: LocaleCode;
	dir: Dir;
	languages: LanguageMeta[];
	setLocale: (code: LocaleCode) => void;
	/** Translate a key. Re-renders on language change because it lives in context. */
	t: (key: string, options?: Record<string, any>) => string;
}

const LanguageContext = createContext<LanguageContextValue | null>(null);

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const [locale, setLocaleState] = useState<LocaleCode>(() => detectInitialLocale());
	// Per product decision: show Arabic text but keep the LTR layout for now (no
	// full RTL mirroring). `dirFor` is kept for when we do RTL layouts later.
	const dir = dirFor(locale);

	// Keep the i18n-js singleton in sync synchronously (before children render) so any
	// I18n.t() called this render — ours or legacy call sites — uses the current locale.
	I18n.locale = locale;

	useEffect(() => {
		document.documentElement.lang = locale;
		document.documentElement.dir = 'ltr';
	}, [locale]);

	const setLocale = useCallback((code: LocaleCode) => {
		try {
			localStorage.setItem(STORAGE_KEY, code);
		} catch {
			/* ignore */
		}
		I18n.locale = code;
		setLocaleState(code);
	}, []);

	const t = useCallback(
		(key: string, options?: Record<string, any>) => I18n.t(key, options),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[locale],
	);

	const value = useMemo(() => ({ locale, dir, languages: LANGUAGES, setLocale, t }), [locale, dir, setLocale, t]);

	return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

/** Access the current locale, dir, switcher and a reactive `t()`. */
export function useT(): LanguageContextValue {
	const ctx = useContext(LanguageContext);
	if (!ctx) {
		// Fallback so components used outside the provider still render (SSR/tests).
		return {
			locale: 'en',
			dir: 'ltr',
			languages: LANGUAGES,
			setLocale: () => undefined,
			t: (key: string, options?: Record<string, any>) => I18n.t(key, options),
		};
	}
	return ctx;
}
